/**
 * PagesRoute — top-level route for the Pages wiki module.
 * Renders a sidebar (LibrarySidebar) on the left and content area on the right.
 * Wraps content in PagesProvider for state management.
 *
 * Includes: RevisionHistory panel, CrossLinkPanel, PublicLibraryBanner,
 * and WebSocket page subscription for real-time update notifications.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PagesProvider, usePagesContext } from '../../state/pages-context';
import { LibrarySidebar } from './LibrarySidebar';
import { PageEditor } from './PageEditor';
import { RevisionHistory } from './RevisionHistory';
import { CrossLinkPanel } from './CrossLinkPanel';
import { RelatedPages } from './RelatedPages';
import { PageSummary } from './PageSummary';
import { PublicLibraryBanner } from './PublicLibraryBanner';
import { WatchButton } from '../../components/WatchButton';
import { PageDiscussion } from './PageDiscussion';
import { LibraryOverview } from './LibraryOverview';
import { usePageSubscription, type LockEvent } from './usePageSubscription';
import { useNavSubscription } from './useNavSubscription';
import { Button } from '../../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import * as pagesApi from '../../api/pages-api';
import { useBreadcrumb } from '../../state/breadcrumb-context';

// ---------------------------------------------------------------------------
// Inner content — consumes PagesContext
// ---------------------------------------------------------------------------

function PagesContent(): React.ReactElement {
  const { libraryId, pageId } = useParams<{ libraryId?: string; pageId?: string }>();
  const { state, selectLibrary, fetchPageTree, fetchPage, clearActivePage } = usePagesContext();
  const { setLibraryTitle, setPageTitle } = useBreadcrumb();
  const [showRightPanel, setShowRightPanel] = useState(false);

  // Real-time left-nav updates. Mounted once here (the stable layout that
  // persists across library/page navigation), NOT in LibrarySidebar which can
  // unmount/remount. One nav socket serves the whole Pages module.
  useNavSubscription();

  // Inline page title rename
  const [isRenamingTitle, setIsRenamingTitle] = useState(false);
  const [titleEditValue, setTitleEditValue] = useState('');
  const titleInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isRenamingTitle && titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
    }
  }, [isRenamingTitle]);

  const startTitleRename = useCallback(() => {
    if (!state.activePage) return;
    setTitleEditValue(state.activePage.title);
    setIsRenamingTitle(true);
  }, [state.activePage]);

  const saveTitleRename = useCallback(async () => {
    const trimmed = titleEditValue.trim();
    setIsRenamingTitle(false);
    if (!state.activePage || !trimmed || trimmed === state.activePage.title) return;
    try {
      await pagesApi.updatePage(state.activePage.pageId, { title: trimmed });
      // Refresh page and tree to reflect new title
      fetchPage(state.activePage.pageId);
      if (libraryId) fetchPageTree(libraryId);
    } catch {
      // Error handled by context
    }
  }, [titleEditValue, state.activePage, fetchPage, fetchPageTree, libraryId]);

  // When libraryId changes in URL, select it and load its tree
  useEffect(() => {
    if (libraryId && libraryId !== state.selectedLibraryId) {
      selectLibrary(libraryId);
      fetchPageTree(libraryId);
    }
    // Set breadcrumb library title via context
    const lib = state.libraries.find((l) => l.libraryId === libraryId);
    setLibraryTitle(lib?.name ?? null);
  }, [libraryId, state.selectedLibraryId, state.libraries, selectLibrary, fetchPageTree, setLibraryTitle]);

  // When pageId changes in URL, fetch the page content or clear active page
  useEffect(() => {
    if (pageId && pageId !== state.activePage?.pageId) {
      fetchPage(pageId);
    } else if (!pageId && state.activePage) {
      clearActivePage();
    }
  }, [pageId, state.activePage?.pageId, fetchPage, clearActivePage]);

  // Set breadcrumb page title via context when active page loads
  useEffect(() => {
    if (state.activePage && state.activePage.pageId === pageId && state.activePage.title) {
      setPageTitle(state.activePage.title);
    } else if (!pageId) {
      setPageTitle(null);
    }
  }, [pageId, state.activePage, setPageTitle]);

  // Clean up breadcrumb titles on unmount
  useEffect(() => {
    return () => {
      setLibraryTitle(null);
      setPageTitle(null);
    };
  }, [setLibraryTitle, setPageTitle]);

  // WebSocket subscription for real-time updates
  const handleReload = useCallback(() => {
    if (pageId) {
      fetchPage(pageId);
    }
  }, [pageId, fetchPage]);

  // Lock event state — bridges WebSocket events to PageEditor's useEditLock hook
  const [lastLockEvent, setLastLockEvent] = useState<LockEvent | null>(null);

  const handleLockEvent = useCallback((event: LockEvent) => {
    setLastLockEvent(event);
  }, []);

  const { banner, dismissBanner, handleReload: reloadFromBanner } = usePageSubscription({
    pageId: pageId ?? null,
    hasUnsavedChanges: state.hasUnsavedChanges,
    onReload: handleReload,
    onLockEvent: handleLockEvent,
  });

  // Determine if current library is public
  const isPublicLibrary = useMemo(() => {
    if (!state.activePage) return false;
    const library = state.libraries.find((l) => l.libraryId === state.activePage?.libraryId);
    const vis = library?.visibility;
    return vis === 'public-read' || vis === 'public-edit' || vis === 'public';
  }, [state.activePage, state.libraries]);

  // Determine if the user has read-only access (viewer role) to the active page
  const isPageReadOnly = state.pageAccessLevel === 'viewer';

  return (
    <div className="flex flex-1 overflow-hidden">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 border-r bg-muted/30 overflow-y-auto" aria-label="Pages sidebar">
        <LibrarySidebar />
      </aside>

      {/* Content area */}
      <main className="flex-1 flex flex-col overflow-hidden" aria-label="Page content">
        {/* Update banner from WebSocket */}
        {banner.visible && (
          <div className="px-6 pt-3">
            <div
              className="bg-blue-50 border border-blue-200 rounded-md px-4 py-2 text-sm text-blue-800 flex items-center justify-between"
              role="alert"
              aria-live="polite"
            >
              <p>Page updated by {banner.updatedBy}.</p>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" className="text-xs h-6" onClick={reloadFromBanner}>
                  Reload
                </Button>
                <Button size="sm" variant="ghost" className="text-xs h-6" onClick={dismissBanner}>
                  Dismiss
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Public library banner */}
        {state.activePage && (
          <div className="px-6 pt-3">
            <PublicLibraryBanner visible={isPublicLibrary} readOnly={isPageReadOnly} />
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-6">
          {state.loading && !state.activePage && (
            <p className="text-muted-foreground" role="status" aria-live="polite">
              Loading...
            </p>
          )}

          {state.error && (
            <p className="text-destructive" role="alert">
              {state.error}
            </p>
          )}

          {!state.loading && !state.error && !state.activePage && !pageId && libraryId && (
            <LibraryOverview libraryId={libraryId} />
          )}

          {!state.loading && !state.error && !state.activePage && !pageId && !libraryId && (
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
              <p className="text-lg">Select a library from the sidebar to get started.</p>
            </div>
          )}

          {state.activePage && (
            <article className="flex flex-col h-full">
              <div className="flex items-center justify-between mb-4 shrink-0">
                {isRenamingTitle ? (
                  <input
                    ref={titleInputRef}
                    type="text"
                    value={titleEditValue}
                    onChange={(e) => setTitleEditValue(e.target.value)}
                    onBlur={saveTitleRename}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') saveTitleRename();
                      if (e.key === 'Escape') setIsRenamingTitle(false);
                    }}
                    className="text-2xl font-semibold px-1 py-0 bg-background border rounded focus:outline-none focus:ring-2 focus:ring-ring flex-1 min-w-0"
                    aria-label="Rename page"
                  />
                ) : (
                  <h1
                    className="text-2xl font-semibold px-0 cursor-pointer hover:bg-muted/50 rounded px-1 -mx-1 transition-colors"
                    onDoubleClick={isPageReadOnly ? undefined : startTitleRename}
                    title={isPageReadOnly ? undefined : 'Double-click to rename'}
                  >
                    {state.activePage.title}
                  </h1>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-xs"
                  onClick={() => setShowRightPanel(!showRightPanel)}
                  aria-expanded={showRightPanel}
                  aria-controls="page-right-panel"
                >
                  {showRightPanel ? 'Hide History & Comments' : 'Show History & Comments'}
                </Button>
                <WatchButton resourceType="page" resourceId={state.activePage.pageId} parentId={state.activePage.libraryId} compact />
              </div>
              <div className="flex-1 -mx-6 -mb-6">
                <PageSummary pageId={state.activePage.pageId} />
                <PageEditor
                  key={state.activePage.pageId}
                  pageId={state.activePage.pageId}
                  libraryId={state.activePage.libraryId}
                  lockEvent={lastLockEvent}
                  readOnly={isPageReadOnly}
                />
              </div>
            </article>
          )}
        </div>
      </main>

      {/* Right panel: Tabbed — Details + Discussion */}
      {showRightPanel && state.activePage && (
        <aside
          id="page-right-panel"
          className="w-72 shrink-0 border-l bg-muted/20 overflow-y-auto p-4 flex flex-col"
          aria-label="Page panel"
        >
          <Tabs defaultValue="details" className="flex flex-col flex-1 min-h-0">
            <TabsList className="mb-3 w-full">
              <TabsTrigger value="details" className="flex-1 text-xs">Details</TabsTrigger>
              <TabsTrigger value="discussion" className="flex-1 text-xs">💬 Discussion</TabsTrigger>
            </TabsList>
            <TabsContent value="details" className="space-y-6 flex-1 overflow-y-auto">
              <RevisionHistory pageId={state.activePage.pageId} />
              <div className="border-t pt-4">
                <CrossLinkPanel pageId={state.activePage.pageId} />
              </div>
              <RelatedPages pageId={state.activePage.pageId} />
            </TabsContent>
            <TabsContent value="discussion" className="flex-1 overflow-y-auto min-h-0">
              <PageDiscussion pageId={state.activePage.pageId} />
            </TabsContent>
          </Tabs>
        </aside>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Route wrapper — wraps content in PagesProvider
// ---------------------------------------------------------------------------

export function PagesRoute(): React.ReactElement {
  return (
    <PagesProvider>
      <div className="flex flex-col h-[calc(100vh-3.5rem)]">
        <PagesContent />
      </div>
    </PagesProvider>
  );
}
